import { handleCommandEvent } from "../../../controller/TranscriptController";
import BubbleType from "../../../model/BubbleType";
import { Constants } from "../../../model/Constants";
import { generateBackgroundImgUrl, isValidColor, sanitizeAndParseJsonData, Timeout } from "../../../util/Misc";

/**
 * util function to handle the display of agent avatar. This function will get the bubble custom element instance through bind
 * @param {Message} message
 */
export function setAgentSpeechAvatar(message) {
    this.speechCon = this.el.firstChild.querySelector(".speechCont");
    const img = this.speechCon.firstElementChild;

    switch (this.speechPos) {
        case "bottom":
            img.classList.add("bottom");
            break;

        case "center":
            img.classList.add("center");
            break;

        // no default
    }

    let imgURL;
    let config;
    let ariaText;

    if (message.getMessageBubble() === BubbleType.OPENER_MESSAGE && this.openerSpeechConfig) {
        config = this.openerSpeechConfig;
    }

    if ((message.getMessageBubble() === BubbleType.VIRTUAL_AGENT_MESSAGE || message.isExternalVA()) && this.vaSpeechConfig) {
        config = this.vaSpeechConfig;
    }

    if (config) {
        imgURL = generateBackgroundImgUrl(config, this.$app);
        ariaText = config.text;
    }

    if ((message.getMessageBubble() === BubbleType.AGENT_MESSAGE || !imgURL) && this.agentSpeechConfig) {
        if (!imgURL) {
            imgURL = generateBackgroundImgUrl(this.agentSpeechConfig, this.$app);
        }
        if (!config) {
            config = this.agentSpeechConfig;
        }
        if (!ariaText) {
            ariaText = config.text;
        }
    }

    const name = message.getName();
    if (config && config.agentInitial) {
       
        img.innerHTML = ["<span>", name[0], "</span>"].join("");

        if (config.agentColors && config.agentColors.length > 0) {
            const index = message.getAgentIndex();
            const len = config.agentColors.length;
            const color = config.agentColors[index % len];

            if (isValidColor(color)) {
                img.style.backgroundColor = color;
            }
        }
        
    } else if (imgURL) {
        img.style.backgroundImage = imgURL;
        img.setAttribute("role", "img");
    }

    img.classList.add(`agent_${name.replace(/\W/g, "")}`);
    if (ariaText) {
        img.setAttribute("aria-label", ariaText);
    }
}

/**
 * Utility code to unify the richMedia redering logic in various Bubble Views
 * @param {RichMain} richInstance - richMedia engine instance
 * @param {CIApp} appInstance - application instance
 * @param {Object} spec - json object containing rich media speck
 * @param {HTMLElement} container - element where rich media renders
 * @param {Boolean} isReloading - Displaying while restoring chat
 * @param {Boolean} shouldReport - reporting needs to happen
 * @param {function=} callback - optional call back
 */
export function renderRichMediaWidget(richInstance, appInstance, spec, container, isReloading?, shouldReport?, callback?) {
    const richRenderParams: any = {
        spec: spec,
        container: container,
        initalView: "",
        isReload: isReloading,
        viewRenderedCb: richAriaOutputs => {
            if (callback) {
                callback(richAriaOutputs);
            }
        },
    };

    if (shouldReport && !isReloading) {
        richRenderParams.viewStartedCb = (nodeID, name, questions) => {
            appInstance.getRichMediaReporter().sendStartedEvent(spec, nodeID, name, questions);
        };

        richRenderParams.viewChangeCb = (oNodeID, oNodeName, oQuestions, oAns, nodeID, nNodeName, nQuestions) => {
            appInstance.getRichMediaReporter().sendRespondedEvent(spec, oNodeID, oNodeName, oQuestions, oAns, nodeID, nNodeName, nQuestions);
        };

        richRenderParams.viewEndedCb = (nodeID, oNodeName, completed, outcome, oQuestions, oAns) => {
            appInstance.getRichMediaReporter().sendEndedEvent(spec, nodeID, oNodeName, completed, outcome, oQuestions, oAns);
        };
    }

    richInstance.renderRichContentinCI(richRenderParams);
}

/*
 * Function that logs datapass event when link is clicked by the user
 * @param {CIApp} $app
 * @param {string} logMode - simple|verbose
 * @param {string} hrefVal
 */
export function logSelectedLink($app, logMode, hrefVal) {
    const datapass: any = {};

    if (logMode === "verbose") {
        datapass.type = hrefVal.startsWith("http") ? "hyperlink" : "deepLink";
        datapass.url = hrefVal;
        datapass.selectedLink = JSON.stringify(datapass);
    } else {
        datapass.selectedLink = hrefVal;
    }

    $app.getSDKInstance().sendDataPass(datapass);
}

/**
 * Utility function to handle Rich Media action.
 * Rich Media instance is unique per rich widget as each widget has itsown logical transtion.
 * @param {CIApp} $app
 * @param {RichView} richInstance
 * @param {boolean} logLinks - should log the links
 */
export function handleRichMediaAction($app, richInstance, logLinks) {
    richInstance.registerCBListener(data => {
        
        if (data.command) {
            if (isCobrowseRichMediaAction(data.command)) {
                actOnCobrowseEvents(data.command.cobrowse, $app.getSDKInstance());
            }
            if (isCustomEventAction(data.command)) {
                handleCommandEvent(data.command.event, $app.getSDKInstance());
            }
        }
        // if masked object is present in sendDataPass then let backend know it appending masking tags and backend will handle it
        if (data.sendDataPass) {
            const { sendDataPass } = data;
            if (sendDataPass?.masked && sendDataPass.masked instanceof Object) {
                const { masked: propertiesToMask } = sendDataPass;
                for (const key in propertiesToMask) {
                    sendDataPass[key] = Constants.MASKING_START_TAG + propertiesToMask[key] + Constants.MASKING_END_TAG;
                }
                delete sendDataPass.masked;
            }
            $app.getSDKInstance().sendDataPass(sendDataPass);
        }
        
        if (data.sendMessage) {
            if (data.sendDataPass) {
                Timeout.set("msgDelayTimer", () => {
                    $app.sendRichMediaData(data.sendMessage.displayText, data.sendMessage);
                }, 300);
            } else {
                $app.sendRichMediaData(data.sendMessage.displayText, data.sendMessage);
            }
        } else if (data.deepLink) {
            if (logLinks) {
                logSelectedLink($app, logLinks, data.deepLink.href);
            }

            $app.bridge.pushToFrameSet(data.deepLink.href, data.deepLink.target);
        } else if (data.closeChat) {
            $app.closeChat();
        }



        const footer = $app.shoutui.getFooter();

        if (footer) {
            footer.enable(false);
        }
    });
}

interface Command {
    cobrowse: Cobrowse;
}

interface Cobrowse {
    state: string;
    shared: boolean;
}

function isCobrowseRichMediaAction({ cobrowse }: Command): boolean {
    return !!cobrowse;
}

function actOnCobrowseEvents({ state, shared }: Cobrowse, sdkInstance) {
    const isShared = !!shared;
    if (state === "start") {
        sdkInstance.startCobrowseSession(isShared);
    } else if (state === "decline") {
        sdkInstance.declineCobrowseInvite(isShared);
    }
}

function isCustomEventAction(command) {
    return command.event;
}

/**
 * @param {BubbleType} type - bubble type to check
 * @return {boolean} - true/false
 */
export function isBubbleTypeRichMediaWidget(type) {
    if (type === BubbleType.RICH_MESSAGE_RECEIPT
        || type === BubbleType.RICH_MESSAGE_FORM
        || type === BubbleType.RICH_MESSAGE_QR
        || type === BubbleType.RICH_MESSAGE_CARD
        || type === BubbleType.RICH_MESSAGE_OTHER
    ) {
        return true;
    }
    return false;
}

/**
 * check if resposive hyperlink contains nuance respose attributes
 * @param {HTMLAnchorElement} anchorElement - anchor element
 * @return {Boolean} Boolean
 */
export function ifResonsiveHyperLinks(anchorElement) {
    const responseAttributes = [Constants.NUANCE_MESSAGE_TEXT, Constants.NUANCE_DATAPASS, Constants.NUANCE_MESSAGE_DATA];
    return responseAttributes.some(attr => anchorElement.getAttribute(attr));
}

/**
 * check if resposive hyperlink contains nuance respose attributes
 * @param {appInstance} $app - application instance
 * @param {Text} text - text to show in customer bubble element
 */
export function linksWithNuanceResponseText($app, text) {
    if (text) {
        $app.onNewCustomerBubble(text);
    }
}

/**
 * check if resposive hyperlink contains "nuance-response-data" attribute
 * and send it as rich media data
 * @param {appInstance} $app - application instance
 * @param {Text} text - text to show in customer bubble element
 * @param {Object} data - text to show in customer bubble element
 */
export function linksWithNuanceResponseData($app, text, data) {
    data = sanitizeAndParseJsonData(data);
    $app.sendResponseLinkData(text, data);
}

/**
 * check if resposive hyperlink contains "nuance-response-datapass" attribute
 * and send it agent as  dataPass
 * @param {appInstance} $app - application instance
 * @param {Object} data - send dataPass object to agent
 */
export function linksWithNuanceDatapass($app, data) {
    data = sanitizeAndParseJsonData(data);
    $app.getSDKInstance().sendDataPass(data);
}
